import { createEntityHooks } from '../core/createEntityHooks'
import type { BaseDocument } from '../core/types'
import useLoginStore from '@/store/LoginStore'

export interface CycleWorkItemDocument extends BaseDocument {
  cycleId: string
  workItemId: string
  statusIdAtClose: string | null
  progressTypeAtClose: string | null
  addedAt: number
  sourceCycleId: string | null
}

const hooks = createEntityHooks<CycleWorkItemDocument>('cycleworkitems')

export const useCycleWorkItems = (filter?: Partial<CycleWorkItemDocument>) => {
  const { data, isLoading, db } = hooks.useEntities(filter)
  return { cycleWorkItems: data, isLoading, db }
}

export const useCycleWorkItemsByCycle = (cycleId: string | undefined) => {
  const envId = useLoginStore((s) => s.workspaceId)
  const enabled = !!envId && !!cycleId
  // Selector keys { envId, cycleId } match the compound index ['envId', 'cycleId'].
  const { data, isLoading, db } = hooks.useEntities(
    enabled
      ? ({ envId, cycleId } as Partial<CycleWorkItemDocument>)
      : undefined,
    { enabled },
  )
  return { cycleWorkItems: enabled ? data : null, isLoading, db }
}

export const useTaskCycleHistory = (workItemId: string | undefined) => {
  const envId = useLoginStore((s) => s.workspaceId)
  const enabled = !!envId && !!workItemId
  // Selector keys { envId, workItemId } match the compound index ['envId', 'workItemId'].
  // Disabled until both ids are in scope so RxDB never runs an un-prefixed selector.
  const { data, isLoading } = hooks.useEntities(
    enabled
      ? ({ envId, workItemId } as Partial<CycleWorkItemDocument>)
      : undefined,
    { enabled },
  )
  return { cycleHistory: enabled ? data : null, isLoading }
}

export const useCycleWorkItemMutations = () => {
  const { create, update, delete: deleteEntity } = hooks.useMutations()
  return {
    createCycleWorkItem: create,
    updateCycleWorkItem: update,
    deleteCycleWorkItem: deleteEntity,
  }
}
